
import { useState, useEffect } from 'react'
import { Navigate, useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { getTesteCompleto } from '../services/database'
import { supabase } from '../lib/supabaseClient'
import { Clock, CheckCircle, XCircle, Play, RotateCcw, ArrowLeft } from 'lucide-react'

// Types based on DB
interface Alternativa {
    id: string
    texto: string
    correta: boolean
}

interface Questao {
    id: string
    enunciado: string
    materia?: string
    dificuldade?: string
    alternativas: Alternativa[]
    resposta_correta_id?: string // Helper to find correct alternative
}

interface TesteData {
    id: string
    title: string
    description?: string | null
    tempo_limite?: number | null
    questoes: Questao[]
}

interface RespostaAluno {
    questao_id: string
    alternativa_id: string
    correta: boolean
    tempo_resposta: number
}

interface FeedbackQuestao {
    correta: boolean
    alternativa_escolhida_id: string
    alternativa_correta_id: string
    explicacao?: string
}

// Componente da Logo do Bizu
const BizuLogo = ({ className = "w-24 h-8" }: { className?: string }) => (
    <img
        src="https://kufrgfjlfhdinewzkwzg.supabase.co/storage/v1/object/public/site/logo.png"
        alt="Bizu Logo"
        className={className}
    />
)

// Componente do Footer
const Footer = () => (
    <footer className="bg-white border-t border-gray-200 py-4 mt-8">
        <div className="max-w-2xl mx-auto px-4 text-center">
            <div className="flex items-center justify-center mb-2">
                <BizuLogo className="w-16 h-5" />
            </div>
            <p className="text-sm text-gray-600">
                © {new Date().getFullYear()} Bizu. Todos os direitos reservados.
            </p>
        </div>
    </footer>
)

const TesteRunnerPage = () => {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { profile, isAuthenticated, loading, user } = useAuth()

    const handleBack = () => {
        if (profile?.role === 'aluno') {
            navigate('/simulado')
        } else {
            navigate('/testes')
        }
    }

    const [teste, setTeste] = useState<TesteData | null>(null)
    const [loadingTeste, setLoadingTeste] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const [testeIniciado, setTesteIniciado] = useState(false)
    const [questaoAtualIndex, setQuestaoAtualIndex] = useState(0)
    const [respostas, setRespostas] = useState<RespostaAluno[]>([])
    const [feedback, setFeedback] = useState<FeedbackQuestao | null>(null)
    const [mostrandoFeedback, setMostrandoFeedback] = useState(false)
    const [testeFinalizado, setTesteFinalizado] = useState(false)
    const [tempoInicio, setTempoInicio] = useState<Date | null>(null)

    useEffect(() => {
        const loadTeste = async () => {
            if (!id) return;
            try {
                setLoadingTeste(true)
                const data = await getTesteCompleto(id)
                if (!data) {
                    setError('Teste não encontrado.')
                    return
                }

                // Transform data to match our needs (identify correct answer ID)
                const questoesFormatadas = data.questoes.map((q: any) => ({
                    ...q,
                    alternativas: q.alternativas,
                    resposta_correta_id: q.alternativas.find((a: any) => a.correta)?.id
                }))

                setTeste({
                    ...data,
                    questoes: questoesFormatadas
                })
            } catch (err) {
                console.error(err)
                setError('Erro ao carregar teste.')
            } finally {
                setLoadingTeste(false)
            }
        }
        loadTeste()
    }, [id])

    const [existingAttempt, setExistingAttempt] = useState<any>(null)

    // Check for existing attempt
    useEffect(() => {
        const checkExistingAttempt = async () => {
            if (!user || !id) return
            try {
                // Check if there is a NON-ARCHIVED attempt
                const { data } = await supabase
                    .from('tentativas_testes')
                    .select('*')
                    .eq('user_id', user.id)
                    .eq('teste_id', id)
                    .eq('is_archived', false)
                    .order('created_at', { ascending: false })
                    .limit(1)
                    .maybeSingle()

                if (data) {
                    setExistingAttempt(data)
                    setTesteIniciado(true) // Skip intro
                    setTesteFinalizado(true) // Go straight to results
                }
            } catch (err) {
                console.error('Error checking attempts:', err)
            }
        }
        checkExistingAttempt()
    }, [user, id])

    const iniciarTeste = () => {
        if (existingAttempt) return // Extra safety
        if (!teste || teste.questoes.length === 0) {
            setError('Nenhuma questão disponível neste teste.')
            return
        }
        setTesteIniciado(true)
        setQuestaoAtualIndex(0)
        setRespostas([])
        setFeedback(null)
        setMostrandoFeedback(false)
        setTesteFinalizado(false)
        setTempoInicio(new Date())
    }

    const responderQuestao = (alternativaId: string) => {
        if (!teste || mostrandoFeedback || existingAttempt) return // Block if existing

        const questao = teste.questoes[questaoAtualIndex]
        const correta = alternativaId === questao.resposta_correta_id
        
        // Proteção básica contra manipulação de tempo
        const agora = new Date().getTime()
        const inicio = tempoInicio?.getTime() || agora
        const tempoReal = (agora - inicio) / 1000
        
        const tempoResposta = Math.min(tempoReal, 3600) // Máximo 1 hora por questão

        const novaResposta: RespostaAluno = {
            questao_id: questao.id,
            alternativa_id: alternativaId,
            correta,
            tempo_resposta: tempoResposta
        }

        setRespostas(prev => [...prev, novaResposta])

        const feedbackAtual: FeedbackQuestao = {
            correta,
            alternativa_escolhida_id: alternativaId,
            alternativa_correta_id: questao.resposta_correta_id || '',
            explicacao: undefined // Database doesn't strictly have explanation on questao table usually, but could add if available
        }

        setFeedback(feedbackAtual)
        setMostrandoFeedback(true)

        // Next question delay
        setTimeout(() => {
            if (questaoAtualIndex + 1 < teste.questoes.length) {
                setQuestaoAtualIndex(prev => prev + 1)
                setMostrandoFeedback(false)
                setFeedback(null)
            } else {
                finalizarTeste()
            }
        }, 2000)
    }

    const finalizarTeste = () => {
        // Validar se todas as questões foram respondidas antes de finalizar
        // Isso evita que o usuário force a finalização via console com dados incompletos
        if (respostas.length < (teste?.questoes.length || 0)) {
            console.warn('Tentativa de finalizar teste incompleto.')
            return
        }
        setTesteFinalizado(true)
    }

    // Effect to save when finished
    useEffect(() => {
        if (testeFinalizado && teste && profile && !mostrandoFeedback) {
            salvarResultado()
        }
    }, [testeFinalizado, teste, profile, mostrandoFeedback])

    const salvarResultado = async () => {
        if (!teste || !profile) return

        // Recalcular no momento de salvar para evitar manipulação de estado local
        const acertos = respostas.filter(r => r.correta).length
        const total = teste.questoes.length
        
        if (total === 0) return

        const percentual = acertos / total
        // Simple TRI simulation: 200 + (percent * 300)
        const nota = Math.round(200 + (percentual * 300))

        try {
            const { error } = await supabase.from('tentativas_testes').insert({
                user_id: profile.id,
                teste_id: teste.id,
                nota,
                acertos,
                total_questoes: total
            } as any)

            if (error) console.error('Erro ao salvar tentativa:', error)
        } catch (err) {
            console.error('Erro save:', err)
        }
    }

    const reiniciarTeste = () => {
        setTesteIniciado(false)
        setQuestaoAtualIndex(0)
        setRespostas([])
        setFeedback(null)
        setMostrandoFeedback(false)
        setTesteFinalizado(false)
        setTempoInicio(null)
    }

    if (loading || loadingTeste) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
        )
    }

    if (!isAuthenticated()) {
        return <Navigate to="/login" replace />
    }

    if (error || !teste) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center p-6 bg-white rounded-lg shadow">
                    <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <p className="text-red-700 font-medium mb-4">{error || 'Teste não encontrado'}</p>
                    <button onClick={handleBack} className="text-primary-600 hover:underline">Voltar</button>
                </div>
            </div>
        )
    }

    // --- LANDING SCREEN ---
    if (!testeIniciado) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-primary-50 to-accent-50 flex flex-col">
                <div className="flex-1 flex items-center justify-center p-4">
                    <div className="max-w-md w-full bg-white rounded-lg shadow-bizu p-8 text-center">
                        <div className="mb-6">
                            <div className="flex justify-center mb-4">
                                <BizuLogo className="w-64 h-20" />
                            </div>
                            <div className="mx-auto w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                                <Play className="w-8 h-8 text-primary-600" />
                            </div>
                            <h1 className="text-2xl font-bold text-primary-900 mb-2">
                                {teste.title}
                            </h1>
                            {teste.description && (
                                <p className="text-gray-600 mb-4">{teste.description}</p>
                            )}
                            <div className="text-sm text-gray-500 mb-4">
                                {teste.questoes.length} Questões {teste.tempo_limite ? `• ${teste.tempo_limite} min` : ''}
                            </div>
                        </div>

                        <div className="mb-6 space-y-2 text-sm text-accent-700">
                            <div className="flex items-center justify-center gap-2">
                                <Clock className="w-4 h-4" />
                                <span>Feedback imediato após cada resposta</span>
                            </div>
                        </div>

                        <button
                            onClick={iniciarTeste}
                            className="w-full bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
                        >
                            <Play className="w-5 h-5" />
                            Iniciar Agora
                        </button>
                    </div>
                </div>
                <Footer />
            </div>
        )
    }

    // --- FINISHED SCREEN ---
    if (testeFinalizado) {
        // Use existing attempt data OR calculated data from current session
        const total = existingAttempt ? existingAttempt.total_questoes : teste.questoes.length
        const acertos = existingAttempt ? existingAttempt.acertos : respostas.filter(r => r.correta).length

        // Percentual calculation
        // Avoid division by zero
        const percentual = total > 0 ? Math.round((acertos / total) * 100) : 0

        const nota = existingAttempt ? existingAttempt.nota : Math.round(200 + ((acertos / total) * 300))

        return (
            <div className="min-h-screen bg-gradient-to-br from-primary-50 to-accent-50 flex flex-col">
                <div className="flex-1 flex items-center justify-center p-4">
                    <div className="max-w-md w-full bg-white rounded-lg shadow-bizu p-8 text-center">
                        <div className="mb-6">
                            <div className="flex justify-center mb-4">
                                <BizuLogo className="w-64 h-20" />
                            </div>
                            <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${percentual >= 70 ? 'bg-green-100' : 'bg-red-100'
                                }`}>
                                {percentual >= 70 ? (
                                    <CheckCircle className="w-8 h-8 text-green-600" />
                                ) : (
                                    <XCircle className="w-8 h-8 text-red-600" />
                                )}
                            </div>
                            <h1 className="text-2xl font-bold text-primary-900 mb-2">
                                {existingAttempt ? 'Resultado Anterior' : 'Teste Finalizado!'}
                            </h1>
                            <p className="text-accent-600">
                                Você acertou {acertos} de {total} questões
                            </p>
                        </div>

                        <div className="mb-6">
                            <div className={`text-5xl font-bold mb-2 ${percentual >= 70 ? 'text-green-600' : 'text-red-600'
                                }`}>
                                {percentual}%
                            </div>
                            <div className="text-sm text-gray-500">Nota TRI: {nota}</div>
                        </div>

                        <div className="space-y-3">
                            {!existingAttempt && (
                                <button
                                    onClick={reiniciarTeste}
                                    className="w-full bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
                                >
                                    <RotateCcw className="w-5 h-5" />
                                    Tentar Novamente
                                </button>
                            )}
                            {existingAttempt && (
                                <div className="p-3 bg-yellow-50 text-yellow-800 text-sm rounded-lg border border-yellow-200">
                                    Você já realizou este teste. Para refazê-lo, solicite permissão ao professor.
                                </div>
                            )}
                            <button
                                onClick={handleBack}
                                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-6 rounded-lg transition-colors"
                            >
                                Voltar para Lista
                            </button>
                        </div>

                    </div>
                </div>
                <Footer />
            </div>
        )
    }

    // --- QUESTION SCREEN ---
    const questao = teste.questoes[questaoAtualIndex]

    return (
        <div className="min-h-screen bg-gradient-to-br from-primary-50 to-accent-50 flex flex-col">
            <div className="flex-1 p-4">
                <div className="max-w-2xl mx-auto">
                    {/* Header */}
                    <div className="bg-white rounded-lg shadow-bizu p-6 mb-6">
                        <div className="flex items-center justify-between mb-4">
                            <button onClick={handleBack} className="text-gray-400 hover:text-gray-600">
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <span className="font-semibold text-gray-700">{teste.title}</span>
                            <div className="w-5"></div>
                        </div>

                        <div className="flex justify-between items-center mb-4">
                            <h1 className="text-xl font-bold text-primary-900">
                                Questão {questaoAtualIndex + 1} de {teste.questoes.length}
                            </h1>
                        </div>
                        <div className="w-full bg-accent-200 rounded-full h-2">
                            <div
                                className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${((questaoAtualIndex + 1) / teste.questoes.length) * 100}%` }}
                            />
                        </div>
                    </div>

                    {/* Questão */}
                    <div className="bg-white rounded-lg shadow-bizu p-6">
                        <h2 className="text-lg font-medium text-primary-900 mb-6 whitespace-pre-wrap">
                            {questao.enunciado}
                        </h2>

                        <div className="space-y-3">
                            {questao.alternativas.map((alternativa) => {
                                const isSelected = feedback?.alternativa_escolhida_id === alternativa.id
                                const isCorrect = alternativa.correta

                                // If showing feedback:
                                // Correct answer: Green
                                // Wrong selected answer: Red
                                // Normal: Gray

                                let buttonClass = 'w-full text-left p-4 rounded-lg border-2 transition-all '

                                if (mostrandoFeedback) {
                                    if (isCorrect) { // Show the correct one always
                                        buttonClass += 'border-green-500 bg-green-50 text-green-800'
                                    } else if (isSelected && !isCorrect) { // Show error if selected wrong
                                        buttonClass += 'border-red-500 bg-red-50 text-red-800'
                                    } else {
                                        buttonClass += 'border-gray-200 bg-gray-50 text-gray-500 opacity-50'
                                    }
                                } else {
                                    buttonClass += 'border-gray-200 hover:border-primary-300 hover:bg-primary-50 cursor-pointer'
                                }

                                return (
                                    <button
                                        key={alternativa.id}
                                        onClick={() => responderQuestao(alternativa.id)}
                                        disabled={mostrandoFeedback}
                                        className={buttonClass}
                                    >
                                        <div className="flex items-center gap-3">
                                            <span className="flex-1">{alternativa.texto}</span>
                                            {mostrandoFeedback && isCorrect && (
                                                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                                            )}
                                            {mostrandoFeedback && isSelected && !isCorrect && (
                                                <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                                            )}
                                        </div>
                                    </button>
                                )
                            })}
                        </div>

                        {/* Feedback */}
                        {mostrandoFeedback && feedback && (
                            <div className={`mt-6 p-4 rounded-lg ${feedback.correta ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                                }`}>
                                <div className={`flex items-center gap-2 mb-2 ${feedback.correta ? 'text-green-800' : 'text-red-800'
                                    }`}>
                                    {feedback.correta ? (
                                        <CheckCircle className="w-5 h-5" />
                                    ) : (
                                        <XCircle className="w-5 h-5" />
                                    )}
                                    <span className="font-medium">
                                        {feedback.correta ? 'Resposta Correta!' : 'Resposta Incorreta'}
                                    </span>
                                </div>
                                <div className="mt-2 text-sm text-accent-600">
                                    Avançando para a próxima questão...
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    )
}

export default TesteRunnerPage
